import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { NodeType } from '../types';

import './TypeFilterLegend.css';

interface LegendEdge {
  from: string;
  to: string;
}

interface PositionedNode {
  id: string;
  name: string;
  color: string;
  x: number;
  y: number;
}

interface TypeFilterLegendProps {
  nodeTypes: NodeType[];
  selectedNodeTypes: string[];
  onNodeTypeFilterChange: (types: string[]) => void;
}

const PADDING_X = 4;
const PADDING_Y = 6;
const LEVEL_GAP = 36;
const COLUMN_GAP = 66;
const SCHEMA_FILTER_COLLAPSED_KEY = 'dewey_schema_filter_collapsed';

const normalizeType = (value: string): string => value.trim();

const getAllTypeIds = (nodeTypes: NodeType[]): string[] =>
  nodeTypes.map((nt) => normalizeType(nt.type));

const buildSchemaEdges = (nodeTypes: NodeType[]): LegendEdge[] => {
  const typeSet = new Set(getAllTypeIds(nodeTypes));
  const edgeKeys = new Set<string>();
  const edges: LegendEdge[] = [];

  for (const nodeType of nodeTypes) {
    const currentTypeId = normalizeType(nodeType.type);
    const rels = nodeType.relationships;
    const buckets = [rels?.one ?? [], rels?.one_to_many ?? [], rels?.zero_to_many ?? []];

    for (const bucket of buckets) {
      for (const entry of bucket) {
        const other = normalizeType(entry.nodeType || '');
        if (!other || !typeSet.has(other)) {
          continue;
        }

        const source = entry.direction === 'in' ? other : currentTypeId;
        const target = entry.direction === 'in' ? currentTypeId : other;
        if (!source || !target || source === target) {
          continue;
        }

        const key = source < target ? `${source}|${target}` : `${target}|${source}`;
        if (edgeKeys.has(key)) {
          continue;
        }
        edgeKeys.add(key);
        edges.push({ from: source, to: target });
      }
    }
  }

  return edges;
};

const computeLevels = (
  typeIds: string[],
  directedEdges: Array<{ from: string; to: string }>
): Map<string, number> => {
  const nodes = new Set(typeIds);
  const indegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();

  for (const id of nodes) {
    indegree.set(id, 0);
    outgoing.set(id, []);
  }

  for (const edge of directedEdges) {
    if (!nodes.has(edge.from) || !nodes.has(edge.to) || edge.from === edge.to) {
      continue;
    }
    outgoing.get(edge.from)?.push(edge.to);
    indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);
  }

  const queue: string[] = [];
  for (const id of typeIds) {
    if ((indegree.get(id) ?? 0) === 0) {
      queue.push(id);
    }
  }

  const level = new Map<string, number>();
  for (const id of typeIds) {
    level.set(id, 0);
  }

  const processed = new Set<string>();
  while (queue.length > 0) {
    const current = queue.shift() as string;
    processed.add(current);
    const currentLevel = level.get(current) ?? 0;

    for (const next of outgoing.get(current) ?? []) {
      level.set(next, Math.max(level.get(next) ?? 0, currentLevel + 1));
      indegree.set(next, (indegree.get(next) ?? 1) - 1);
      if ((indegree.get(next) ?? 0) === 0) {
        queue.push(next);
      }
    }
  }

  // Fallback for cycles: place unprocessed nodes at level 0.
  for (const id of typeIds) {
    if (!processed.has(id) && !level.has(id)) {
      level.set(id, 0);
    }
  }

  return level;
};

const TypeFilterLegend: React.FC<TypeFilterLegendProps> = ({
  nodeTypes,
  selectedNodeTypes,
  onNodeTypeFilterChange,
}) => {
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 18, y: 18 });
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(SCHEMA_FILTER_COLLAPSED_KEY) === '1'
  );
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const allTypeIds = useMemo(() => getAllTypeIds(nodeTypes), [nodeTypes]);
  const isAllSelected =
    selectedNodeTypes.length === 0 || selectedNodeTypes.length === allTypeIds.length;

  const { positionedNodes, edges, width, height } = useMemo(() => {
    const typeById = new Map(nodeTypes.map((nt) => [normalizeType(nt.type), nt]));
    const ids = allTypeIds;
    const directedEdges: Array<{ from: string; to: string }> = [];

    for (const nt of nodeTypes) {
      const rels = nt.relationships;
      const buckets = [rels?.one ?? [], rels?.one_to_many ?? [], rels?.zero_to_many ?? []];
      const currentTypeId = normalizeType(nt.type);
      for (const bucket of buckets) {
        for (const entry of bucket) {
          const other = normalizeType(entry.nodeType || '');
          if (!other || !typeById.has(other) || other === currentTypeId) {
            continue;
          }
          if (entry.direction === 'in') {
            directedEdges.push({ from: other, to: currentTypeId });
          } else {
            directedEdges.push({ from: currentTypeId, to: other });
          }
        }
      }
    }

    const levels = computeLevels(ids, directedEdges);
    const groupedByLevel = new Map<number, string[]>();
    for (const id of ids) {
      const lvl = levels.get(id) ?? 0;
      const arr = groupedByLevel.get(lvl) ?? [];
      arr.push(id);
      groupedByLevel.set(lvl, arr);
    }

    const sortedLevels = Array.from(groupedByLevel.keys()).sort((a, b) => a - b);
    for (const lvl of sortedLevels) {
      groupedByLevel.get(lvl)?.sort((a, b) => a.localeCompare(b));
    }

    const maxColumns = Math.max(1, ...Array.from(groupedByLevel.values()).map((v) => v.length));
    const mapWidth = Math.max(210, PADDING_X * 2 + maxColumns * COLUMN_GAP);
    const mapHeight = Math.max(96, PADDING_Y * 2 + Math.max(1, sortedLevels.length) * LEVEL_GAP);

    const positioned: PositionedNode[] = [];
    for (const lvl of sortedLevels) {
      const members = groupedByLevel.get(lvl) ?? [];
      const totalColumns = members.length;
      const rowCenterOffset = ((totalColumns - 1) * COLUMN_GAP) / 2;

      members.forEach((id, columnIdx) => {
        const typeDef = typeById.get(id);
        if (!typeDef) {
          return;
        }
        const x =
          PADDING_X + columnIdx * COLUMN_GAP + (maxColumns * COLUMN_GAP) / 2 - rowCenterOffset;
        const y = PADDING_Y + lvl * LEVEL_GAP;
        positioned.push({
          id,
          name: typeDef.name || id,
          color: typeDef.color || '#ccd7e2',
          x,
          y,
        });
      });
    }

    return {
      positionedNodes: positioned,
      edges: buildSchemaEdges(nodeTypes),
      width: mapWidth,
      height: mapHeight,
    };
  }, [allTypeIds, nodeTypes]);

  const posById = useMemo(() => {
    const map = new Map<string, PositionedNode>();
    for (const node of positionedNodes) {
      map.set(node.id, node);
    }
    return map;
  }, [positionedNodes]);

  const isSelected = (typeId: string): boolean => {
    if (isAllSelected) {
      return true;
    }
    return selectedNodeTypes.includes(typeId);
  };

  const handleToggleNodeType = (typeId: string): void => {
    const selectedSet = new Set(isAllSelected ? allTypeIds : selectedNodeTypes);
    if (selectedSet.has(typeId)) {
      selectedSet.delete(typeId);
      const next = Array.from(selectedSet);
      onNodeTypeFilterChange(next.length === 0 ? [] : next);
      return;
    }

    selectedSet.add(typeId);
    const next = Array.from(selectedSet);
    if (next.length === allTypeIds.length) {
      onNodeTypeFilterChange([]);
      return;
    }
    onNodeTypeFilterChange(next);
  };

  const startDrag = (event: React.MouseEvent<HTMLDivElement>): void => {
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    event.preventDefault();
  };

  useEffect(() => {
    const onMove = (event: MouseEvent): void => {
      const state = dragStateRef.current;
      if (!state) {
        return;
      }
      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      setPosition({ x: state.originX + dx, y: state.originY + dy });
    };

    const onUp = (): void => {
      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(SCHEMA_FILTER_COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  return (
    <div
      className="type-filter-legend"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      aria-label="Node type filter legend"
    >
      <div className="type-filter-legend__header" onMouseDown={startDrag}>
        <strong>Schema Filter</strong>
        <div className="type-filter-legend__header-actions">
          <button
            type="button"
            className="type-filter-legend__collapse-btn"
            onClick={(event) => {
              event.stopPropagation();
              setCollapsed((prev) => !prev);
            }}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand legend' : 'Collapse legend'}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="type-filter-legend__actions">
            <button
              type="button"
              className={`type-filter-legend__all-btn${isAllSelected ? ' is-selected' : ''}`}
              onClick={() => onNodeTypeFilterChange([])}
              aria-pressed={isAllSelected}
            >
              All
            </button>
          </div>

          <svg
            className="type-filter-legend__map"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Schema relationship map"
          >
            {edges.map((edge) => {
              const fromNode = posById.get(edge.from);
              const toNode = posById.get(edge.to);
              if (!fromNode || !toNode) {
                return null;
              }
              const active = isSelected(fromNode.id) && isSelected(toNode.id);
              return (
                <line
                  key={`${edge.from}|${edge.to}`}
                  x1={fromNode.x}
                  y1={fromNode.y}
                  x2={toNode.x}
                  y2={toNode.y}
                  className={`type-filter-legend__edge${active ? ' is-active' : ''}`}
                />
              );
            })}

            {positionedNodes.map((node) => {
              const selected = isSelected(node.id);
              return (
                <g
                  key={node.id}
                  className={`type-filter-legend__node${selected ? ' is-selected' : ''}`}
                  transform={`translate(${node.x}, ${node.y})`}
                  onClick={() => handleToggleNodeType(node.id)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleToggleNodeType(node.id);
                    }
                  }}
                >
                  <circle r={8} fill={node.color} />
                  <text x={11} y={4}>
                    {node.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </>
      )}
    </div>
  );
};

export default TypeFilterLegend;
